/**
 * Gruppiert E-Mail-Benachrichtigungen einer Buchung nach ihrer Mailjet-`messageID`,
 * sodass pro physisch versendeter Mail genau EINE Tabellenzeile entsteht.
 *
 * Hintergrund: Beim Versand einer Mail MIT Rechnungsanhang schreibt das Backend pro
 * physischem Send zwei `EmailNotification`-Datensätze (templateType `FreeText` +
 * `SettlementConfirmation`/`SettledBookingCancellation`), die sich dieselbe `messageID`
 * teilen. Es ist EINE Mail, doppelt protokolliert. Dieser rein clientseitige, immutable
 * Helper fasst solche Paare wieder zu einer Anzeige-Zeile zusammen.
 *
 * Bewusst strukturell typisiert (Pick der benötigten Felder via `GroupableNotification`),
 * damit der Helper isoliert testbar bleibt und nicht vom vollen Codegen-Typ abhängt.
 *
 * Node-14/TS-4.7-konform: kein `.at()`, `.findLast()`, `.replaceAll()`, `satisfies`,
 * `structuredClone()`, `Object.hasOwn()`, `Promise.any()`.
 */

/**
 * Minimale Anhang-Form. `fileName` dient als Dedup-Schlüssel und kann defensiv fehlen.
 */
type GroupableAttachment = {
    fileName?: string | null;
    [key: string]: unknown;
};

/**
 * Strukturelle Mindestform einer gruppierbaren Benachrichtigung. Restliche Felder des
 * konkreten Tabellen-Typs `T` werden 1:1 durchgereicht (Generic + Spread).
 */
type GroupableNotification = {
    id: string;
    /** nullable: messageID ist seit dem Schema-Fix optional (Mollie-Mails ohne Mailjet-ID);
     *  kann "", null oder undefined sein. Die Gruppierung behandelt das bereits (G2). */
    messageID?: string | null;
    templateType?: string | null;
    createDate?: string | null;
    attachments?: ReadonlyArray<unknown> | null;
};

/**
 * Macht aus einem optionalen (Readonly-)Array eine flache, kopierte Liste. Niemals das
 * Original mutieren (G5).
 */
function toArray(
    x: ReadonlyArray<unknown> | null | undefined,
): Array<unknown> {
    return Array.isArray(x) ? [...x] : [];
}

/**
 * Vereinigt die Anhänge aller Partner einer Gruppe und entfernt Duplikate anhand des
 * Dateinamens. Fehlt der `fileName`, dient `JSON.stringify` defensiv als Fallback-Key,
 * damit identische anonyme Anhänge dennoch dedupliziert werden.
 */
function dedupeAttachmentsByFileName(atts: Array<unknown>): Array<unknown> {
    const seen = new Set<string>();
    const out: Array<unknown> = [];

    for (const a of atts) {
        const fileName = (a as GroupableAttachment | null)?.fileName ?? "";
        // Fallback-Key, falls fileName fehlt -> identische Objekte kollabieren trotzdem.
        const key = fileName !== "" ? fileName : JSON.stringify(a);

        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        out.push(a);
    }

    return out;
}

/**
 * Gruppiert Benachrichtigungen nach `messageID` (G1: ausschließlich nach messageID,
 * NICHT nach templateType ausblenden). Pro Gruppe entsteht genau eine Zeile.
 *
 * Guards:
 * - G2: leere `messageID` (`""`/Whitespace) wird NIE kollabiert -> jede Notification
 *   einzeln mit Original-`id` durchgereicht.
 * - G3: Gruppen mit nur einem Eintrag bleiben unverändert (Original-Objekt, Original-`id`).
 * - G4: die gemergte Zeile trägt die `FreeText`-`id` (damit `GetEmailNotificationById`
 *   im EmailDetailModal weiterhin auflöst); Fallback = erster Eintrag, falls kein
 *   `FreeText` vorhanden. Niemals synthetische/konkatenierte IDs.
 * - G5: immutable — Eingangsarray und dessen Objekte werden nie mutiert.
 * - G6: `createDate` ist KEINE Merge-Bedingung (nur `messageID` zählt) und dient
 *   ausschließlich als Sortierschlüssel.
 *
 * @param notifications ungeordnete, schreibgeschützte Liste der Benachrichtigungen
 * @returns neue Liste mit einer Zeile pro physischer Mail, nach `createDate` DESC sortiert
 */
export function groupNotificationsByMessageId<T extends GroupableNotification>(
    notifications: ReadonlyArray<T>,
): Array<T> {
    // key = messageID; Wert = alle Notifications mit dieser messageID
    const groups = new Map<string, Array<T>>();
    // leere messageID -> NIE kollabieren (G2)
    const standalone: Array<T> = [];

    // 1) Einsammeln — Reihenfolge der Erstankunft je messageID bleibt erhalten.
    for (const n of notifications) {
        // "0" = Sentinel statt echter Mailjet-ID (lokaler Dev-Transport, Error-Sends):
        // wie leer behandeln, sonst kollabieren ALLE solchen Mails zu einer Zeile.
        const trimmed = n.messageID?.trim();
        const key = trimmed && trimmed !== "" && trimmed !== "0" ? n.messageID : null;

        if (key === null) {
            standalone.push(n); // G2: leere messageID einzeln, Original-id
            continue;
        }

        const bucket = groups.get(key);
        if (bucket) {
            bucket.push(n);
        } else {
            groups.set(key, [n]);
        }
    }

    // 2) Pro Gruppe genau EINE Anzeige-Zeile bauen.
    const merged: Array<T> = [];
    groups.forEach((bucket) => {
        if (bucket.length === 1) {
            merged.push(bucket[0]); // G3: Solo-Gruppe unverändert
            return;
        }

        // G4: Anzeige-Zeile trägt die FreeText-id; Fallback = erster Eintrag.
        const freeText = bucket.filter(
            (b) => b.templateType === "FreeText",
        )[0];
        const base = freeText ?? bucket[0];

        // Anhänge aller Partner vereinen + nach fileName deduplizieren.
        const mergedAttachments = dedupeAttachmentsByFileName(
            bucket.reduce(
                (acc, b) => acc.concat(toArray(b.attachments)),
                [] as Array<unknown>,
            ),
        );

        // TYP-FEINHEIT: `{...base, attachments: <neu>}` kollidiert strukturell mit `T`,
        // weil `attachments` an `T` (Codegen) i.d.R. eine konkretere Anhang-Form trägt
        // als die hier defensiv vereinte `Array<unknown>`-Liste. Wir bauen daher das
        // Objekt über das Basis-Objekt auf und überschreiben nur die `attachments`-Property
        // gezielt; der lokal begründete Cast nach `T` ist sicher, da alle restlichen
        // Felder unverändert aus `base` stammen und `attachments` zur Laufzeit dieselbe
        // Element-Struktur behält (nur dedupliziert/vereint). So vermeiden wir `any`.
        const mergedRow = {...base} as T & {attachments: Array<unknown>};
        mergedRow.attachments = mergedAttachments;

        merged.push(mergedRow);
    });

    // 3) Solo-Zeilen (leere messageID) wieder anhängen.
    const result = merged.concat(standalone);

    // G6: deterministische Reihenfolge — createDate DESC (Resolver liefert ungeordnet).
    return result.sort(
        (a, b) =>
            new Date(b.createDate ?? 0).getTime() -
            new Date(a.createDate ?? 0).getTime(),
    );
}
